/**
 * LogTable - Virtualized log table with color-coded levels
 * 
 * Uses react-window for efficient rendering of 1000+ logs.
 * Features:
 * - Color-coded log levels
 * - Truncated messages with expand
 * - Auto-scroll toggle
 * - Row click to open detail modal
 * - Duration bar visualization
 * - Source location column
 * - Sortable columns
 * - Slow operation highlighting
 */

import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { List, ListImperativeAPI } from 'react-window';
import { Copy, ChevronUp, ChevronDown, ArrowUpDown, ChevronRight, ToggleLeft, ToggleRight, Layers, Maximize2, Sparkles } from 'lucide-react';
import { JsonTreeView } from './JsonTreeView';
import { StackTraceViewer } from './StackTraceViewer';
import type { LogEvent } from '../../../utils/WeBelieveLogger.types';

type SortField = 'timestamp' | 'level' | 'component' | 'duration_ms';
type SortDirection = 'asc' | 'desc';

interface LogTableProps {
  logs: LogEvent[];
  onRowClick: (log: LogEvent, index: number) => void;
  autoScroll: boolean;
  onAutoScrollChange: (enabled: boolean) => void;
  isLoading?: boolean;
  searchTerm?: string;
  expandAll?: boolean;
  onExpandAllChange?: (enabled: boolean) => void;
  highlightedLogIds?: Set<string>;
  /** Callback when user clicks "Explain" button on a log row */
  onExplainLog?: (log: LogEvent, contextLogs: LogEvent[]) => void;
}

const LEVEL_STYLES: Record<string, { bg: string; text: string; badge: string }> = {
  debug: { bg: 'bg-gray-50 dark:bg-gray-900', text: 'text-gray-600 dark:text-gray-400', badge: 'bg-gray-500' },
  info: { bg: 'bg-blue-50 dark:bg-blue-950', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-500' },
  warn: { bg: 'bg-yellow-50 dark:bg-yellow-950', text: 'text-yellow-700 dark:text-yellow-300', badge: 'bg-yellow-500' },
  error: { bg: 'bg-red-50 dark:bg-red-950', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-500' },
  performance: { bg: 'bg-green-50 dark:bg-green-950', text: 'text-green-700 dark:text-green-300', badge: 'bg-green-500' },
};

const ROW_HEIGHT = 40;

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }) + '.' + date.getMilliseconds().toString().padStart(3, '0');
}

function truncateMessage(message: string, maxLength: number = 80): string {
  if (message.length <= maxLength) return message;
  return message.slice(0, maxLength) + '...';
}

// Highlight search term in text
function highlightSearchTerm(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm || searchTerm.length < 2) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  if (parts.length === 1) return text;
  
  return parts.map((part, i) => 
    regex.test(part) ? (
      <mark key={i} className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white px-0.5 rounded">
        {part}
      </mark>
    ) : part
  );
}

// Format source location for display
function formatSource(source?: { file?: string; line?: number }): string {
  if (!source?.file) return '';
  
  let file = source.file;
  
  // Remove src/ prefix if present for cleaner display
  if (file.startsWith('src/')) {
    file = file.slice(4);
  }
  
  // Add line number if available
  const line = source.line;
  return line ? `${file}:${line}` : file;
}

// Duration thresholds for color coding (in ms)
const DURATION_THRESHOLDS = {
  fast: 10,    // < 10ms = green
  normal: 50,  // < 50ms = yellow
  slow: 100,   // < 100ms = orange
  // >= 100ms = red
};

function getDurationColor(durationMs: number): string {
  if (durationMs < DURATION_THRESHOLDS.fast) return 'text-green-600 dark:text-green-400';
  if (durationMs < DURATION_THRESHOLDS.normal) return 'text-yellow-600 dark:text-yellow-400';
  if (durationMs < DURATION_THRESHOLDS.slow) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400 font-semibold';
}

// Calculate duration bar width (max 100px for visual)
function getDurationBarWidth(durationMs: number, maxDuration: number): number {
  if (maxDuration === 0) return 0;
  return Math.min(100, (durationMs / maxDuration) * 100);
}

function getDurationBarColor(durationMs: number): string {
  if (durationMs < DURATION_THRESHOLDS.fast) return 'bg-green-500';
  if (durationMs < DURATION_THRESHOLDS.normal) return 'bg-yellow-500';
  if (durationMs < DURATION_THRESHOLDS.slow) return 'bg-orange-500';
  return 'bg-red-500';
}

/**
 * Determine if a log is "explainable" - worth showing the AI explain button
 * Shows for: error, warn, performance issues, or messages containing keywords
 */
function isExplainableLog(log: LogEvent): boolean {
  // Always show for error and warn levels
  if (log.level === 'error' || log.level === 'warn') return true;

  // Show for slow performance logs (> 100ms)
  if (log.level === 'performance' && log.duration_ms && log.duration_ms > 100) return true;

  // Check message for interesting keywords
  const keywords = ['fail', 'error', 'exception', 'timeout', 'retry', 'recovery', 'gap', 'slow', 'crash', 'abort'];
  const msgLower = log.message.toLowerCase();
  return keywords.some(kw => msgLower.includes(kw));
}

// Expanded row detail panel - shows JSON data and stack trace (DevTools style)
function ExpandedRowDetail({ log }: { log: LogEvent }) {
  const hasData = log.data && Object.keys(log.data).length > 0;
  const hasStackTrace = log.source?.stack && log.source.stack.length > 0;

  // If nothing to show, display minimal message
  if (!hasData && !hasStackTrace) {
    return (
      <div className="px-6 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">No data</span>
      </div>
    );
  }

  return (
    <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 space-y-3">
      {/* Stack Trace Section */}
      {hasStackTrace && log.source && (
        <StackTraceViewer
          source={log.source}
          defaultExpanded={false}
          showFrameNumbers={true}
          enableSearch={false}
        />
      )}

      {/* JSON Data Section */}
      {hasData && (
        <div>
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Data
          </div>
          <JsonTreeView data={log.data!} initialExpandDepth={2} />
        </div>
      )}
    </div>
  );
}

// Expandable row component for non-virtualized mode
function ExpandableLogRow({
  log,
  index,
  onRowClick,
  maxDuration,
  searchTerm,
  isExpanded,
  onToggleExpand,
  isHighlighted = false,
  onExplainLog,
  allLogs,
}: {
  log: LogEvent;
  index: number;
  onRowClick: (log: LogEvent, index: number) => void;
  maxDuration: number;
  searchTerm: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isHighlighted?: boolean;
  onExplainLog?: (log: LogEvent, contextLogs: LogEvent[]) => void;
  allLogs: LogEvent[];
}) {
  const levelStyle = LEVEL_STYLES[log.level] || LEVEL_STYLES.debug;
  const sourceText = formatSource(log.source);
  const hasDuration = log.duration_ms !== undefined && log.duration_ms !== null;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRowClick(log, index);
  };

  // Single click expands row inline (DevTools style)
  const handleRowClick = () => {
    onToggleExpand();
  };

  // Handle AI Explain button click - gather context logs and call callback
  const handleExplain = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onExplainLog) return;

    // Get context: ~10 logs before and ~5 after from the same session
    const sessionId = log.session_id;
    const logTime = new Date(log.timestamp).getTime();

    // Filter logs from same session and sort by timestamp
    const sessionLogs = allLogs
      .filter(l => l.session_id === sessionId)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Find current log index in session logs
    const currentIdx = sessionLogs.findIndex(l =>
      l.timestamp === log.timestamp && l.message === log.message
    );

    // Get context window: 10 before, current, 5 after
    const startIdx = Math.max(0, currentIdx - 10);
    const endIdx = Math.min(sessionLogs.length, currentIdx + 6); // +6 to include current + 5 after
    const contextLogs = sessionLogs.slice(startIdx, endIdx);

    onExplainLog(log, contextLogs);
  };

  const showExplainButton = !!onExplainLog;

  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <div
        onClick={handleRowClick}
        className={`flex items-center px-4 h-10 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${levelStyle.bg} ${isHighlighted ? 'ring-2 ring-inset ring-lex-accent bg-lex-accent-soft' : ''}`}
      >
        {/* Expand toggle indicator */}
        <div className="w-6 flex-shrink-0 p-0.5 text-gray-400">
          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>

        {/* Timestamp */}
        <div className="w-24 flex-shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400" title={log.timestamp}>
          {formatTimestamp(log.timestamp)}
        </div>

        {/* Level Badge */}
        <div className="w-16 flex-shrink-0">
          <span className={`inline-block px-1.5 py-0.5 text-xs font-medium text-white rounded ${levelStyle.badge}`}>
            {log.level === 'performance' ? 'PERF' : log.level.toUpperCase()}
          </span>
        </div>

        {/* Component */}
        <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300 truncate" title={log.component}>
          {highlightSearchTerm(log.component, searchTerm)}
        </div>

        {/* Message */}
        <div className={`flex-1 min-w-0 text-sm truncate ${levelStyle.text}`} title={log.message}>
          {highlightSearchTerm(truncateMessage(log.message, 60), searchTerm)}
        </div>

        {/* Source + Stack Trace Indicator */}
        <div className="w-96 flex-shrink-0 flex items-center gap-1">
          <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate" title={log.source?.file || ''}>
            {highlightSearchTerm(sourceText, searchTerm)}
          </span>
          {log.source?.stack && log.source.stack.length > 0 && (
            <span title={`Stack trace: ${log.source.stack.length} frames`}>
              <Layers className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
            </span>
          )}
        </div>

        {/* Duration */}
        <div className="w-24 flex-shrink-0 flex items-center justify-end gap-1">
          {hasDuration && log.duration_ms !== undefined && (
            <>
              <div className="w-12 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getDurationBarColor(log.duration_ms)} transition-all`}
                  style={{ width: `${getDurationBarWidth(log.duration_ms, maxDuration)}%` }}
                />
              </div>
              <span className={`text-xs font-mono ${getDurationColor(log.duration_ms)}`}>
                {log.duration_ms >= 1000 
                  ? `${(log.duration_ms / 1000).toFixed(1)}s`
                  : `${log.duration_ms.toFixed(0)}ms`
                }
              </span>
            </>
          )}
        </div>

        {/* Actions: Explain + Copy + Open Modal */}
        <div className="w-20 flex-shrink-0 flex justify-end gap-1">
          {showExplainButton && (
            <button
              onClick={handleExplain}
              className="p-1 text-purple-500 hover:text-purple-700 dark:hover:text-purple-300 transition-colors hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded"
              title="AI Explain this log"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={handleCopy}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            title="Copy JSON"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleOpenModal}
            className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
            title="Open detail modal"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded detail panel */}
      {isExpanded && <ExpandedRowDetail log={log} />}
    </div>
  );
}

const LogRowComponent = ({ index, style, logs, onRowClick, onToggleExpand, maxDuration, searchTerm, highlightedLogIds, onExplainLog }: any) => {
  const log = logs[index];

  if (!log) return null;

  const levelStyle = LEVEL_STYLES[log.level] || LEVEL_STYLES.debug;
  const sourceText = formatSource(log.source);
  const hasDuration = log.duration_ms !== undefined && log.duration_ms !== null;
  // Use composite key for highlighting since LogEvent doesn't have id field
  const logKey = `${log.timestamp}-${log.session_id}-${log.component}`;
  const isHighlighted = highlightedLogIds?.has(logKey);
  const showExplainButton = !!onExplainLog;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRowClick(log, index);
  };

  // Single click expands row (switches to non-virtualized mode)
  const handleClick = () => {
    onToggleExpand(index);
  };

  // Handle AI Explain button click - gather context logs and call callback
  const handleExplain = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onExplainLog) return;

    // Get context: ~10 logs before and ~5 after from the same session
    const sessionId = log.session_id;

    // Filter logs from same session and sort by timestamp
    const sessionLogs = logs
      .filter((l: LogEvent) => l.session_id === sessionId)
      .sort((a: LogEvent, b: LogEvent) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Find current log index in session logs
    const currentIdx = sessionLogs.findIndex((l: LogEvent) =>
      l.timestamp === log.timestamp && l.message === log.message
    );

    // Get context window: 10 before, current, 5 after
    const startIdx = Math.max(0, currentIdx - 10);
    const endIdx = Math.min(sessionLogs.length, currentIdx + 6);
    const contextLogs = sessionLogs.slice(startIdx, endIdx);

    onExplainLog(log, contextLogs);
  };

  return (
    <div
      style={style}
      onClick={handleClick}
      className={`flex items-center px-4 border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${levelStyle.bg} ${isHighlighted ? 'ring-2 ring-inset ring-lex-accent bg-lex-accent-soft' : ''}`}
    >
      {/* Expand indicator */}
      <div className="w-6 flex-shrink-0 p-0.5 text-gray-400">
        <ChevronRight className="w-4 h-4" />
      </div>

      {/* Timestamp */}
      <div className="w-24 flex-shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400" title={log.timestamp}>
        {formatTimestamp(log.timestamp)}
      </div>

      {/* Level Badge */}
      <div className="w-16 flex-shrink-0">
        <span className={`inline-block px-1.5 py-0.5 text-xs font-medium text-white rounded ${levelStyle.badge}`}>
          {log.level === 'performance' ? 'PERF' : log.level.toUpperCase()}
        </span>
      </div>

      {/* Component */}
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300 truncate" title={log.component}>
        {highlightSearchTerm(log.component, searchTerm)}
      </div>

      {/* Message */}
      <div className={`flex-1 min-w-0 text-sm truncate ${levelStyle.text}`} title={log.message}>
        {highlightSearchTerm(truncateMessage(log.message, 60), searchTerm)}
      </div>

      {/* Source + Stack Trace Indicator */}
      <div className="w-96 flex-shrink-0 flex items-center gap-1">
        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate" title={log.source?.file || ''}>
          {highlightSearchTerm(sourceText, searchTerm)}
        </span>
        {log.source?.stack && log.source.stack.length > 0 && (
          <span title={`Stack trace: ${log.source.stack.length} frames`}>
            <Layers className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
          </span>
        )}
      </div>

      {/* Duration with bar visualization (for performance logs) */}
      <div className="w-24 flex-shrink-0 flex items-center justify-end gap-1">
        {hasDuration && (
          <>
            {/* Duration bar */}
            <div className="w-12 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getDurationBarColor(log.duration_ms)} transition-all`}
                style={{ width: `${getDurationBarWidth(log.duration_ms, maxDuration)}%` }}
              />
            </div>
            {/* Duration text */}
            <span className={`text-xs font-mono ${getDurationColor(log.duration_ms)}`}>
              {log.duration_ms >= 1000 
                ? `${(log.duration_ms / 1000).toFixed(1)}s`
                : `${log.duration_ms.toFixed(0)}ms`
              }
            </span>
          </>
        )}
      </div>

      {/* Actions: Explain + Copy + Open Modal */}
      <div className="w-20 flex-shrink-0 flex justify-end gap-1">
        {showExplainButton && (
          <button
            onClick={handleExplain}
            className="p-1 text-purple-500 hover:text-purple-700 dark:hover:text-purple-300 transition-colors hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded"
            title="AI Explain this log"
          >
            <Sparkles className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={handleCopy}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          title="Copy JSON"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleOpenModal}
          className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
          title="Open detail modal"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Sortable column header component
function SortableHeader({ 
  label, 
  field, 
  currentSort, 
  currentDirection, 
  onSort, 
  className 
}: { 
  label: string; 
  field: SortField; 
  currentSort: SortField; 
  currentDirection: SortDirection; 
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const isActive = currentSort === field;
  
  return (
    <button
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${className || ''}`}
    >
      {label}
      {isActive ? (
        currentDirection === 'asc' ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-50" />
      )}
    </button>
  );
}

export function LogTable({
  logs,
  onRowClick,
  autoScroll,
  onAutoScrollChange,
  isLoading = false,
  searchTerm = '',
  expandAll = false,
  onExpandAllChange,
  highlightedLogIds = new Set(),
  onExplainLog,
}: LogTableProps) {
  const listRef = useRef<ListImperativeAPI | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLogsLengthRef = useRef(logs.length);
  const userScrolledRef = useRef(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Individual row expansion state (for when expandAll is false)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  // Toggle individual row expansion
  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);
  
  // Clear expanded rows when logs change significantly
  useEffect(() => {
    setExpandedRows(new Set());
  }, [logs.length]);
  
  // Handle sort toggle
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);
  
  // Sort logs
  const sortedLogs = useMemo(() => {
    const sorted = [...logs].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'timestamp':
          comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          break;
        case 'level':
          const levelOrder = { error: 0, warn: 1, info: 2, performance: 3, debug: 4 };
          comparison = (levelOrder[a.level as keyof typeof levelOrder] || 5) - 
                       (levelOrder[b.level as keyof typeof levelOrder] || 5);
          break;
        case 'component':
          comparison = (a.component || '').localeCompare(b.component || '');
          break;
        case 'duration_ms':
          comparison = (a.duration_ms || 0) - (b.duration_ms || 0);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [logs, sortField, sortDirection]);
  
  // Calculate max duration for bar visualization
  const maxDuration = useMemo(() => {
    return Math.max(...logs.map(l => l.duration_ms || 0), 1);
  }, [logs]);

  // Auto-scroll to top when new logs arrive (logs are newest-first)
  useEffect(() => {
    if (autoScroll && logs.length > prevLogsLengthRef.current && !userScrolledRef.current) {
      listRef.current?.scrollToRow({ index: 0, align: 'start' });
    }
    prevLogsLengthRef.current = logs.length;
  }, [logs.length, autoScroll]);

  // Detect user scroll to disable auto-scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollTop > 100) {
      userScrolledRef.current = true;
      if (autoScroll) {
        onAutoScrollChange(false);
      }
    } else {
      userScrolledRef.current = false;
      if (!autoScroll) {
        onAutoScrollChange(true);
      }
    }
  }, [autoScroll, onAutoScrollChange]);

  // Memoize row props to prevent unnecessary re-renders
  // Note: index and style are provided by react-window, we only pass custom props
  const rowProps = useMemo(() => ({
    logs: sortedLogs,
    onRowClick,
    onToggleExpand: toggleRowExpansion,
    maxDuration,
    searchTerm,
    highlightedLogIds,
    onExplainLog,
  }), [sortedLogs, onRowClick, toggleRowExpansion, maxDuration, searchTerm, highlightedLogIds, onExplainLog]);

  if (logs.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <div className="text-lg font-medium mb-2">No logs match your filters</div>
          <div className="text-sm">Try adjusting your filter criteria or wait for new logs</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 flex flex-col bg-white dark:bg-gray-900 relative">
      {/* Table Header */}
      <div className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {/* Expand All Toggle - or placeholder for alignment */}
        {onExpandAllChange ? (
          <button
            onClick={() => onExpandAllChange(!expandAll)}
            className={`w-6 flex-shrink-0 p-0.5 transition-colors ${expandAll ? 'text-lex-accent' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
            title={expandAll ? 'Collapse all rows' : 'Expand all rows'}
          >
            {expandAll ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          </button>
        ) : (
          <div className="w-6 flex-shrink-0" />
        )}
        <div className="w-24 flex-shrink-0">
          <SortableHeader 
            label="Time" 
            field="timestamp" 
            currentSort={sortField} 
            currentDirection={sortDirection} 
            onSort={handleSort} 
          />
        </div>
        <div className="w-16 flex-shrink-0">
          <SortableHeader 
            label="Level" 
            field="level" 
            currentSort={sortField} 
            currentDirection={sortDirection} 
            onSort={handleSort} 
          />
        </div>
        <div className="w-32 flex-shrink-0">
          <SortableHeader 
            label="Component" 
            field="component" 
            currentSort={sortField} 
            currentDirection={sortDirection} 
            onSort={handleSort} 
          />
        </div>
        <div className="flex-1 min-w-0">Message</div>
        <div className="w-96 flex-shrink-0">Source</div>
        <div className="w-24 flex-shrink-0 text-right">
          <SortableHeader 
            label="Duration" 
            field="duration_ms" 
            currentSort={sortField} 
            currentDirection={sortDirection} 
            onSort={handleSort}
            className="justify-end"
          />
        </div>
        <div className="w-20 flex-shrink-0"></div>
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && !expandAll && (
        <button
          onClick={() => {
            onAutoScrollChange(true);
            listRef.current?.scrollToRow({ index: 0, align: 'start' });
          }}
          className="absolute bottom-20 right-8 z-10 flex items-center gap-1 px-3 py-2 bg-lex-accent text-lex-accent-contrast text-sm rounded-full shadow-lg hover:bg-lex-accent-hover transition-colors"
        >
          <ChevronUp className="w-4 h-4" />
          New logs available
        </button>
      )}

      {/* Expandable List (non-virtualized) - used when expandAll is enabled or rows are individually expanded */}
      {(expandAll || expandedRows.size > 0) ? (
        <div className="flex-1 overflow-auto">
          {sortedLogs.map((log, index) => (
            <ExpandableLogRow
              key={`${log.timestamp}-${index}`}
              log={log}
              index={index}
              onRowClick={onRowClick}
              maxDuration={maxDuration}
              searchTerm={searchTerm}
              isExpanded={expandAll || expandedRows.has(index)}
              onToggleExpand={() => toggleRowExpansion(index)}
              isHighlighted={highlightedLogIds.has(`${log.timestamp}-${log.session_id}-${log.component}`)}
              onExplainLog={onExplainLog}
              allLogs={sortedLogs}
            />
          ))}
        </div>
      ) : (
        /* Virtualized List - used for normal mode without expansions */
        <div className="flex-1 overflow-hidden">
          <List
            listRef={listRef}
            defaultHeight={600}
            rowCount={sortedLogs.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={LogRowComponent}
            rowProps={rowProps}
            overscanCount={10}
            onScroll={handleScroll}
          />
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        </div>
      )}
    </div>
  );
}
